import React, { useRef, ReactNode } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';
import cn from 'classnames';

import { createSx } from '../../utils';
import { tooltipStyles } from './overrides';
import Tooltip from '.';

const sx = createSx({
  typography: {
    '&.singleline': {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&.multiline': {
      display: '-webkit-box',
      maxWidth: '100%',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
  },
  childrenWrapper: (theme) => ({
    '& *': tooltipStyles({ theme }),
  }),
});

interface Props extends Omit<TypographyProps, 'title'> {
  children: ReactNode;
  title?: ReactNode;
  maxLines?: number;
  testID?: string;
  disableTooltip?: boolean;
}

const EllipsedTypography = ({
  title,
  testID,
  children,
  className,
  maxLines = 1,
  variant = 'inherit',
  disableTooltip = false,
  sx: typographySx,
  color: typographyColor,
  ...typographyProps
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const isMultiLine = maxLines > 1;

  const shouldOpen = () => {
    if (disableTooltip) return false;
    const el = contentRef.current;
    if (!el) return false;

    if (isMultiLine) {
      return el.scrollHeight > el.clientHeight;
    }

    const range = document.createRange();
    range.selectNodeContents(el);
    const textWidth = range.getBoundingClientRect().width;
    const actualWidth = el.getBoundingClientRect().width;

    return textWidth > actualWidth + 0.01; // Adding 0.01px tolerance for rounding issues
  };

  const typographyStyle = {
    ...sx.typography,
    ...typographySx,
  };

  return (
    <Tooltip
      shouldOpen={shouldOpen}
      title={title || <Box sx={sx.childrenWrapper}>{children}</Box>}
    >
      <Typography
        {...typographyProps}
        variant={variant}
        data-test-id={testID}
        component='span'
        className={cn(isMultiLine ? 'multiline' : 'singleline', className)}
        color={typographyColor}
        sx={{
          ...typographyStyle,
          WebkitLineClamp: isMultiLine ? maxLines : 'none',
        }}
        ref={contentRef}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};

export default EllipsedTypography;
